import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { RefreshTokenService } from '../services/refresh-token.service';
import { AuthService } from '../services/auth.service';
import { LoggerService } from '../services/logger.service';

/**
 * Functional HTTP Interceptor for automatic JWT token refresh.
 *
 * Intercepts 401 Unauthorized responses and attempts to refresh the access token.
 * On success, retries the original request. On failure, logs out the user.
 *
 * @example
 * ```typescript
 * // In app.config.ts
 * provideHttpClient(
 *   withInterceptors([retryInterceptor, apiHttpInterceptor, authRefreshInterceptor])
 * )
 * ```
 */
export const authRefreshInterceptor: HttpInterceptorFn = (req, next) => {
  const refreshTokenService = inject(RefreshTokenService);
  const authService = inject(AuthService);
  const router = inject(Router);
  const logger = inject(LoggerService);

  const EXCLUDED_URLS = ['/login', '/register', '/token/refresh'];

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Only handle 401 Unauthorized
      if (error.status !== 401) {
        return throwError(() => error);
      }

      // Don't refresh on excluded URLs
      const isExcluded = EXCLUDED_URLS.some((url) => req.url.includes(url));
      if (isExcluded) {
        logger.debug('Skipping refresh for excluded URL', { url: req.url });
        return throwError(() => error);
      }

      // Don't refresh if user didn't opt for "keep me connected"
      if (!refreshTokenService.canRefresh()) {
        logger.debug('Skipping refresh: keepConnection not enabled');
        return throwError(() => error);
      }

      // Attempt token refresh
      logger.debug('Handling 401 error, attempting token refresh');

      return refreshTokenService.refreshToken().pipe(
        switchMap(() => {
          // Refresh successful, retry the original request
          logger.debug('Token refreshed, retrying original request', {
            url: req.url,
          });
          return next(req);
        }),
        catchError((refreshError) => {
          // Refresh failed - logout and redirect
          logger.warn('Token refresh failed, logging out user', {
            status: refreshError.status,
            url: req.url,
          });

          authService.logout();
          router.navigate(['/login'], {
            queryParams: {
              returnUrl: router.url,
              message: 'session_expired',
            },
          });

          return throwError(() => refreshError);
        }),
      );
    }),
  );
};
